import { useEffect } from "react";
import { match } from "path-to-regexp";
import { useLocation } from "react-router-dom";
import { useQuery } from "@apollo/client";
import { useGlobal } from "reactn";
import { GET_USER_STATE } from "@ugg/shared/api/requests/accounts/user-state";
import { GET_USER_SETTINGS } from "@ugg/shared/api/graphql/queries";
import { AppRoutes } from "@ugg/shared/routes/app-routes";
import { addCookie, removeCookie } from "@ugg/shared/utils/cookies";
import { normalizeEmail, sha256Hex } from "lib/hashed-email";
import { FreestarPlacements } from "./freestar-placements";
import { FreestarSlot } from "./FreestarInContent";

export { FREESTAR_PUBLISHER, FreestarPlacements } from "./freestar-placements";

const NO_AD_ROUTES = [
  AppRoutes.LOGIN,
  AppRoutes.SIGNUP,
  AppRoutes.AUTH,
  AppRoutes.AUTH_CALLBACK,
  AppRoutes.AUTH_CREATE,
  AppRoutes.AUTH_LINK,
  AppRoutes.AUTH_LINK_MORE,
  AppRoutes.AUTH_DONE,
  AppRoutes.AUTH_ERROR,
  AppRoutes.RECOVER_ACCOUNT,
  AppRoutes.RESET_PASSWORD,
  AppRoutes.PLUS,
  AppRoutes.CHECKOUT,
  AppRoutes.CHECKOUT_STEP,
  AppRoutes.UGG_APP_SPLASH,
  AppRoutes.YEAR_IN_REVIEW,
  AppRoutes.PROFILE_YEAR_IN_REVIEW,
].map((route) => match(route));

// Takeover units stay off the homepage.
const isHome = match(AppRoutes.HOME);

// Once pubfig is live, queue.push runs the callback synchronously, so a failure inside the ad stack
// (for example deleteStickyFooter not yet defined because its module is still loading) would surface
// as a React error and crash the page. Ad failures must never take the page down.
function freestarQueue(fn: (freestar: FreestarGlobal) => void) {
  window.freestar = window.freestar ?? ({ queue: [] } as unknown as FreestarGlobal);
  window.freestar.queue.push(() => {
    try {
      if (window.freestar) fn(window.freestar);
    } catch (error) {
      console.warn("[freestar] queued call failed", error);
    }
  });
}

// pubfig counts the initial page load itself; module scope so it survives the footer remounting.
let isFirstPageview = true;

// The footer and the floating video player are created one tick late so a redirect that fires in a mount effect
// does not create, delete, and re-create it back to back.
function StickyFooter({ pathname }: { pathname: string }) {
  useEffect(() => {
    let created = false;
    const timer = setTimeout(() => {
      created = true;
      if (!isFirstPageview) freestarQueue((freestar) => freestar.trackPageview());
      isFirstPageview = false;
      freestarQueue((freestar) => freestar.newStickyFooter(FreestarPlacements.BottomRail));
      freestarQueue((freestar) => freestar.newVideo(FreestarPlacements.Video));
    });

    return () => {
      clearTimeout(timer);
      if (created) {
        freestarQueue((freestar) => freestar.deleteVideo(FreestarPlacements.Video));
        freestarQueue((freestar) => freestar.deleteStickyFooter(FreestarPlacements.BottomRail));
      }
    };
  }, [pathname]);

  return null;
}

interface UserSettingsData {
  getSettings?: {
    email?: string | null;
    marketingOptOut?: boolean | null;
  } | null;
}

// Hashed email passthrough: the address is normalized and SHA-256 hashed in the browser,
// so the plain email never reaches Freestar's script or any ad vendor.
function useHashedEmailPassthrough(enabled: boolean) {
  const [authToken] = useGlobal("authToken");
  const { data } = useQuery<UserSettingsData>(GET_USER_SETTINGS, { skip: !enabled || !authToken });
  const email = data?.getSettings?.marketingOptOut ? null : data?.getSettings?.email;

  useEffect(() => {
    const normalized = email ? normalizeEmail(email) : null;
    if (!normalized || !window.crypto?.subtle) return;

    let cancelled = false;
    sha256Hex(normalized).then((sha256) => {
      if (!cancelled) freestarQueue((freestar) => freestar.identity.setIdentity({ hashes: { sha256 } }));
    });

    return () => {
      cancelled = true;
    };
  }, [email]);
}

// Wunderkind's tag, as supplied by Freestar. It only bootstraps tag.bounceexchange.com/<id>/i.js.
// The bx_tracker GAM macro from the supplied tag is left out: outside an ad server it is never
// substituted, and the script ignores it.
function WunderkindTag() {
  useEffect(() => {
    if (document.getElementById("bx-tag")) return;
    const script = document.createElement("script");
    script.id = "bx-tag";
    script.async = true;
    script.charset = "utf-8";
    script.src = "https://assets.bounceexchange.com/assets/bounce/iframebuster.js";
    script.setAttribute("data-website-id", "8903");
    document.head.appendChild(script);
  }, []);

  return null;
}

export const FreestarContainer = () => {
  const { pathname } = useLocation();
  const { loading, data } = useQuery(GET_USER_STATE);
  const isPremium = !!data?.getState.premium.isPremium;

  useHashedEmailPassthrough(!loading && !isPremium);

  // cookie + window flag to disable Freestar Recovered for PLUS users
  useEffect(() => {
    if (loading) return;
    window.disable_fsr = isPremium;
    if (isPremium) {
      addCookie("disable_fsr", "true");
    } else {
      removeCookie("disable_fsr");
    }
  }, [loading, isPremium]);

  // Ad-Shield is configured to skip recovery when this is true. It stays undefined until the
  // user state has loaded, so "unknown" is never mistaken for "not premium".
  useEffect(() => {
    if (!loading) window.uggIsPremium = isPremium;
  }, [loading, isPremium]);

  if (loading || isPremium) return null;
  if (NO_AD_ROUTES.some((matches) => matches(pathname))) return null;

  return (
    <>
      <WunderkindTag />
      <StickyFooter pathname={pathname} />
      {!isHome(pathname) && <FreestarSlot placement={FreestarPlacements.OopFlex} slotId="oop-flex" refreshOnSearch={false} />}
    </>
  );
};
